import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import type {
  KioskFlowApiResponse,
  KioskFlowNameRequest,
  KioskFlowOperatorContext,
  KioskFlowPublishPrepareResponse,
  KioskFlowRecordVersionRequest,
  KioskFlowSaveRequest,
  KioskFlowVersionResponse,
  KioskAssetItem,
  KioskAssetListResponse,
} from '../types/kiosk-flow-types';

const ASSETS_PAGE_SIZE = 500;

export class KioskFlowApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  // Operator headers come from the operator picked on the page, not the global store.
  private async getAuthHeaders(ctx: KioskFlowOperatorContext): Promise<Record<string, string>> {
    const headers = await this.tokenManager.getAuthHeaders();
    const result: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': ctx.operatorId.toString(),
    };
    if (ctx.appId && ctx.appId !== 0) {
      result['X-APP-ID'] = ctx.appId.toString();
    }
    return result;
  }

  private flowUrl(operatorId: number): string {
    return `${this.baseUrl}/kiosk-flow/operators/${operatorId}/flow`;
  }

  async getFlow(ctx: KioskFlowOperatorContext): Promise<KioskFlowApiResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      this.flowUrl(ctx.operatorId),
      { method: 'GET', headers: await this.getAuthHeaders(ctx) },
      'KioskFlowApi.getFlow',
      'GET /kiosk-flow/operators/{operatorId}/flow'
    );
    return response.json();
  }

  async createFlow(ctx: KioskFlowOperatorContext, request: KioskFlowNameRequest): Promise<KioskFlowApiResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      this.flowUrl(ctx.operatorId),
      { method: 'POST', headers: await this.getAuthHeaders(ctx), body: JSON.stringify(request) },
      'KioskFlowApi.createFlow',
      'POST /kiosk-flow/operators/{operatorId}/flow'
    );
    return response.json();
  }

  async renameFlow(ctx: KioskFlowOperatorContext, request: KioskFlowNameRequest): Promise<KioskFlowApiResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      this.flowUrl(ctx.operatorId),
      { method: 'PUT', headers: await this.getAuthHeaders(ctx), body: JSON.stringify(request) },
      'KioskFlowApi.renameFlow',
      'PUT /kiosk-flow/operators/{operatorId}/flow'
    );
    return response.json();
  }

  // Saves the name and, when screenFlow is sent, replaces the stored draft
  async saveFlow(ctx: KioskFlowOperatorContext, request: KioskFlowSaveRequest): Promise<KioskFlowApiResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      this.flowUrl(ctx.operatorId),
      { method: 'PUT', headers: await this.getAuthHeaders(ctx), body: JSON.stringify(request) },
      'KioskFlowApi.saveFlow',
      'PUT /kiosk-flow/operators/{operatorId}/flow'
    );
    return response.json();
  }

  // The operator's kiosk XML layouts from Asset Manager (all screen sizes)
  async getLayouts(ctx: KioskFlowOperatorContext): Promise<KioskAssetListResponse> {
    return this.listAssets(ctx, 'Xml', 'KioskFlowApi.getLayouts');
  }

  // The operator's kiosk images from Asset Manager — the promo banner is picked from these
  async getImages(ctx: KioskFlowOperatorContext): Promise<KioskAssetListResponse> {
    return this.listAssets(ctx, 'Image', 'KioskFlowApi.getImages');
  }

  // Pages through skip/take until totalCount is reached, or a page comes back short
  private async listAssets(
    ctx: KioskFlowOperatorContext,
    assetType: 'Xml' | 'Image',
    apiName: string
  ): Promise<KioskAssetListResponse> {
    const headers = await this.getAuthHeaders(ctx);
    const items: KioskAssetItem[] = [];
    for (;;) {
      const query = `applicationType=EnergyAndroidKiosk&assetType=${assetType}&operatorId=${ctx.operatorId}&skip=${items.length}&take=${ASSETS_PAGE_SIZE}`;
      const response = await ApiErrorMiddleware.fetchWithErrorHandling(
        `${this.baseUrl}/assets?${query}`,
        { method: 'GET', headers },
        apiName,
        'GET /assets'
      );
      const page: KioskAssetListResponse = await response.json();
      if (page?.isSuccess === false) return page;

      const pageItems = page?.items ?? [];
      items.push(...pageItems);
      const total = page?.totalCount;
      const done = total != null ? items.length >= total : pageItems.length < ASSETS_PAGE_SIZE;
      if (done || pageItems.length === 0) {
        return { ...page, items, totalCount: total ?? items.length };
      }
    }
  }

  // Validates the saved draft and returns the JSON to push to the kiosks. Writes nothing.
  async preparePublish(ctx: KioskFlowOperatorContext): Promise<KioskFlowPublishPrepareResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.flowUrl(ctx.operatorId)}/publish/prepare`,
      { method: 'POST', headers: await this.getAuthHeaders(ctx) },
      'KioskFlowApi.preparePublish',
      'POST /kiosk-flow/operators/{operatorId}/flow/publish/prepare'
    );
    return response.json();
  }

  // Records the publish once the kiosks have the JSON — rejected when the draft changed since prepare
  async recordVersion(
    ctx: KioskFlowOperatorContext,
    request: KioskFlowRecordVersionRequest
  ): Promise<KioskFlowVersionResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      `${this.flowUrl(ctx.operatorId)}/versions`,
      { method: 'POST', headers: await this.getAuthHeaders(ctx), body: JSON.stringify(request) },
      'KioskFlowApi.recordVersion',
      'POST /kiosk-flow/operators/{operatorId}/flow/versions'
    );
    return response.json();
  }

  async deleteFlow(ctx: KioskFlowOperatorContext): Promise<KioskFlowApiResponse> {
    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      this.flowUrl(ctx.operatorId),
      { method: 'DELETE', headers: await this.getAuthHeaders(ctx) },
      'KioskFlowApi.deleteFlow',
      'DELETE /kiosk-flow/operators/{operatorId}/flow'
    );
    return response.json();
  }
}
